import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { CWC_WHATS_NEW } from '../../config'

// Dark gradient over the image bottom so the white pills stay legible.
const GradientOverlay = withCitrusTheme(Box, () => ({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, rgba(12, 9, 11, 0) 45%, rgba(12, 9, 11, 0.5) 100%)',
}))

// On mobile the pills sit at the image's left edge (112px column), so they must start below the
// overhanging NEW badge (which reaches ~36px down) to stay readable; on tablet/desktop the pills
// are centred and clear the badge horizontally, so they sit at the Figma y=24 offset.
const PillsLayout = withCitrusTheme(Box, (theme) => [
  theme.responsiveStyle(['16px', null, '24px'], ['paddingTop']),
])

// Fully-rounded pill (single-row) — "Next day delivery", and "Adjust portions" from tablet up.
const Pill = withCitrusTheme(Box, () => ({
  borderRadius: '999px',
  boxShadow: '0 2px 8px rgba(12, 9, 11, 0.42)',
}))

// "Adjust portions" surface: a fully-rounded single-row pill from tablet up, but a rounded-rectangle
// two-row pill on mobile (where the label and stepper stack to fit the 112px image column).
const AdjustPill = withCitrusTheme(Box, (theme) => [
  { boxShadow: '0 2px 8px rgba(12, 9, 11, 0.42)' },
  theme.responsiveStyle(['12px', null, '999px'], ['borderRadius']),
])

// Smaller on mobile so both pills fit over the 112px mobile image column.
const PillLabel = withCitrusTheme(Text, (theme) => [
  { fontWeight: 700, color: '#1A2026', whiteSpace: 'nowrap' },
  theme.responsiveStyle(['9px', null, '11px'], ['fontSize']),
  theme.responsiveStyle(['14px', null, '15px'], ['lineHeight']),
])

// Decorative 5-bar "2 of 5" portions indicator: 2 filled bars (Radical Red #FF3355) and 3 unfilled
// (Iron #D7DBDF). Neither hex is a Citrus token, so they are set exactly here.
const StepperBarFilled = withCitrusTheme(Box, (theme) => [
  { borderRadius: '2px', backgroundColor: '#FF3355' },
  theme.responsiveStyle(['3px', null, '4px'], ['width']),
  theme.responsiveStyle(['8px', null, '11px'], ['height']),
])
const StepperBarEmpty = withCitrusTheme(Box, (theme) => [
  { borderRadius: '2px', backgroundColor: '#D7DBDF' },
  theme.responsiveStyle(['3px', null, '4px'], ['width']),
  theme.responsiveStyle(['8px', null, '11px'], ['height']),
])

const FILLED_PORTIONS = 2
const TOTAL_PORTIONS = 5

const StepperGraphic = () => (
  <Box
    display={Display.Flex}
    flexDirection={FlexDirection.Row}
    alignItems={AlignItems.Center}
    gap={2}
  >
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Row}
      alignItems={AlignItems.Center}
      gap={0.5}
    >
      {Array.from({ length: TOTAL_PORTIONS }, (_, bar) =>
        bar < FILLED_PORTIONS ? <StepperBarFilled key={bar} /> : <StepperBarEmpty key={bar} />,
      )}
    </Box>
    <PillLabel>{String(FILLED_PORTIONS)}</PillLabel>
  </Box>
)

export const CWCFloatingPills = () => (
  <Box
    data-testid="whatsNewFloatingPills"
    aria-hidden
    position={DisplayPosition.Absolute}
    top="0"
    left="0"
    width="100%"
    height="100%"
  >
    <GradientOverlay />
    <PillsLayout
      position={DisplayPosition.Absolute}
      top="0"
      left="0"
      width="100%"
      height="100%"
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      justifyContent={JustifyContent.FlexStart}
      alignItems={AlignItems.Center}
      gap={[1, null, 2]}
    >
      <Pill bg={Color.White} paddingV={[0.5, null, 1]} paddingH={[2, null, 3]}>
        <PillLabel>{CWC_WHATS_NEW.pills.nextDayDelivery}</PillLabel>
      </Pill>
      <AdjustPill
        bg={Color.White}
        paddingV={[1, null, 1]}
        paddingH={[2, null, 3]}
        display={Display.Flex}
        flexDirection={[FlexDirection.Column, null, FlexDirection.Row]}
        alignItems={AlignItems.Center}
        gap={[0.5, null, 2]}
      >
        <PillLabel>{CWC_WHATS_NEW.pills.adjustPortions}</PillLabel>
        <StepperGraphic />
      </AdjustPill>
    </PillsLayout>
  </Box>
)
